import { Fragment, useState, useEffect } from "react";
import ModalNew from "./Modal";
import PopupHeader from "./common/popupHeader";
import PopupContent from "./common/PopupContent";
import PopupSection from "./common/PopupSection";
import ApiResponseModal from "./ApiResponseModal";
import { createSupportRequest } from "../../../api/account/supportAPI";
import { useGetAccountQuery } from "../../../api/account";
import SmallAlert from "../alerts/SmallAlert";
import getAlertTitle from "../../../utils/getAlertTitle";
import classes from "./Modal.module.css";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";

function SupportPopUp({ setIsModalOpen }) {
  const [comment, setComment] = useState("");
  const [requestType, setRequestType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertType, setAlertType] = useState(null);
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);

  // ApiResponseModal states
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isResultLoading, setIsResultLoading] = useState(false);
  const [isResultSuccess, setIsResultSuccess] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const {
    data: accountInfo,
    isLoading: isAccountLoading,
    isError,
  } = useGetAccountQuery();

  // disable once they've submitted or while submitting
  const disabled = sent || isSubmitting;

  useEffect(() => {
    let timer;
    if (sent && secondsLeft > 0) {
      timer = setInterval(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [sent, secondsLeft]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlertType(null);
    setMessage("");

    // Open result modal and show loading
    setIsResultModalOpen(true);
    setIsResultLoading(true);
    setIsResultSuccess(false);

    try {
      const apiResponse = await createSupportRequest({
        email: accountInfo?.email,
        user_name: accountInfo?.user_name,
        comment,
        request_type: requestType,
      });

      const data = await apiResponse.json();

      if (apiResponse.ok) {
        setIsResultSuccess(true);
        setResultMessage(
          "Thank you for contacting us! We'll get back to you as soon as possible.",
        );
      } else {
        setIsResultSuccess(false);
        setResultMessage(
          data.message || "Failed to send support request. Please try again.",
        );
      }
    } catch (error) {
      setIsResultSuccess(false);
      setResultMessage(error.message || "An error occurred. Please try again.");
      console.error("Error submitting support request:", error);
    } finally {
      setIsSubmitting(false);
      setIsResultLoading(false);
    }
  };

  const handleClose = () => {
    setAlertType(null);
    setMessage("");
    setComment("");
    setRequestType("");
    setSent(false);
    setSecondsLeft(5);
    setIsModalOpen(false);
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    if (isResultSuccess) {
      handleClose(); // Close the main modal on success
    }
  };

  const handleRetry = () => {
    setIsResultModalOpen(false);
    // Reset form for retry
    setAlertType(null);
    setMessage("");
  };

  if (isAccountLoading) {
    return (
      <Fragment>
        <ModalNew className="content-modal">
          <PopupHeader
            title="Contact Support"
            setOpenState={setIsModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/support_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <div className={classes.modalLoadingContainer}>
              <LoadingPlaceholder />
            </div>
          </PopupContent>
        </ModalNew>
      </Fragment>
    );
  }

  if (isError) {
    return (
      <Fragment>
        <ModalNew className="content-modal">
          <PopupHeader
            title="Contact Support"
            setOpenState={setIsModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/support_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <div className={classes.modalErrorContainer}>
              <SmallAlert
                alert={true}
                type="error"
                title="Error"
                message="Could not load account information. Please try again later."
              />
              <button
                type="button"
                onClick={handleClose}
                className="btn btnBox"
              >
                <b>Close</b>
              </button>
            </div>
          </PopupContent>
        </ModalNew>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {!isResultModalOpen && (
        <ModalNew className="content-modal">
          <PopupHeader
            title="Contact Support"
            setOpenState={setIsModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/support_blue_popup.svg`}
            variant="compact"
          />

          <PopupContent>
            <PopupSection>
              Need a hand or have a question? Submit a quick support request —
              our team will get back to you shortly.
            </PopupSection>

            {alertType && (
              <div className="alert-container" style={{ marginBottom: "20px" }}>
                <SmallAlert
                  alert={true}
                  title={getAlertTitle(alertType)}
                  type={alertType}
                  message={message}
                  dismissOnClickOutside={true}
                  dismissOnFocusOutside={false}
                />
              </div>
            )}

            {!sent && (
              <div className={classes.modalFormSection}>
                <div className="form-field">
                  <input
                    type="email"
                    value={accountInfo?.user_name || ""}
                    disabled
                    required
                    className="form-input"
                    placeholder="Email"
                  />
                </div>

                <div className="form-field">
                  <select
                    name="supportType"
                    value={requestType}
                    onChange={(e) => setRequestType(e.target.value)}
                    disabled={disabled}
                    className="form-input"
                    required
                  >
                    <option value="" disabled>
                      Select a support type
                    </option>
                    <option value="ACT">Account Manage</option>
                    <option value="BLL">Billing Question</option>
                    <option value="TCH">Technical Problem</option>
                    <option value="FEA">Feature Request</option>
                    <option value="FED">Feedback / Suggestion</option>
                    <option value="BUG">Bug Report</option>
                    <option value="GNR">General Question</option>
                    <option value="PVC">Privacy / Security</option>
                    <option value="LEG">Legal / Compliance</option>
                    <option value="OTH">Other</option>
                  </select>
                </div>

                <div className="form-field">
                  <textarea
                    rows="5"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    required
                    disabled={disabled}
                    maxLength={500}
                    className="form-textarea"
                    placeholder="Write comment..."
                  ></textarea>
                  <small className="character-count">
                    {comment.length}/500 characters
                  </small>
                </div>

                {isSubmitting && (
                  <div className={classes.modalLoadingContainer}>
                    <LoadingPlaceholder />
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleSubmit}
                  className={`cta-button primary ${disabled ? "disabled" : ""}`}
                  data-icon={isSubmitting ? "hourglass_empty" : "check_circle"}
                  disabled={disabled}
                >
                  <b>{isSubmitting ? "Sending..." : "Send Request"}</b>
                </button>
              </div>
            )}
          </PopupContent>
        </ModalNew>
      )}

      {/* ApiResponseModal */}
      <ApiResponseModal
        isOpen={isResultModalOpen}
        isLoading={isResultLoading}
        isSuccess={isResultSuccess}
        message={resultMessage}
        onClose={handleCloseResultModal}
        onRetry={handleRetry}
      />
    </Fragment>
  );
}

export default SupportPopUp;
